import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Camera, AlertCircle, FileText, CheckCircle, User, Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const EditEmployeeModal = ({
  showEditModal,
  setShowEditModal,
  editViewOnly,
  selectedEmployee,
  detailTab,
  setDetailTab,
  historyLoading,
  historyData,
  renderDetailHistory,
  resetForm,
  formData,
  setFormData,
  handleEditEmployee,
  handleFileUpload,
  branches,
  positions,
  professions,
  selectedSupports,
  setSelectedSupports,
  newSalaryAmount,
  setNewSalaryAmount,
  handleSalaryUpdate
}) => {
  return (
    <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
      <DialogContent className={`${editViewOnly ? 'max-w-5xl' : 'max-w-2xl'} max-h-[85vh] overflow-y-auto`} data-testid="edit-employee-modal">
        <DialogHeader>
          <DialogTitle>{editViewOnly ? `Çalışan Detayı — ${selectedEmployee?.name || ''}` : 'Çalışan Düzenle'}</DialogTitle>
          <DialogDescription>{editViewOnly ? 'İşten ayrılan çalışanın bilgileri ve tüm çalışma dönemi geçmişi (salt-okunur)' : 'Çalışan bilgilerini güncelleyin'}</DialogDescription>
        </DialogHeader>

        {editViewOnly && selectedEmployee?.termination_date && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>İşten Ayrılmış Personel</AlertTitle>
            <AlertDescription>
              Bu personel {new Date(selectedEmployee.termination_date).toLocaleDateString('tr-TR')} tarihinde işten ayrılmıştır. 
              {selectedEmployee.termination_reason && ` Ayrılma nedeni: ${selectedEmployee.termination_reason}`}
            </AlertDescription>
          </Alert>
        )}

        {editViewOnly && (
          <div className="flex flex-wrap gap-1 border-b border-[hsl(var(--border))] mb-2" data-testid="detail-tabs">
            {[
              { key: 'genel', label: 'Genel Bilgiler' },
              { key: 'mesai', label: 'Mesai' },
              { key: 'izinler', label: 'İzinler' },
              { key: 'avanslar', label: 'Avanslar' },
              { key: 'maas', label: 'Maaş' },
            ].map(t => (
              <button
                key={t.key}
                type="button"
                onClick={() => setDetailTab(t.key)}
                data-testid={`detail-tab-${t.key}`}
                className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${detailTab === t.key ? 'bg-[hsl(var(--brand-700))] text-white' : 'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]'}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}
        {editViewOnly && detailTab !== 'genel' && (
          <div className="py-2" data-testid="detail-history-panel">
            {historyLoading ? (
              <div className="flex items-center justify-center py-16 text-[hsl(var(--muted-foreground))]">
                <Loader2 className="h-6 w-6 animate-spin mr-2" /> Geçmiş yükleniyor...
              </div>
            ) : !historyData ? (
              <div className="text-center py-16 text-[hsl(var(--muted-foreground))]">Geçmiş verisi bulunamadı.</div>
            ) : (
              <>{renderDetailHistory()}</>
            )}
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => { setShowEditModal(false); resetForm(); }}>Kapat</Button>
            </DialogFooter>
          </div>
        )}

        {(!editViewOnly || detailTab === 'genel') && (
        <form onSubmit={handleEditEmployee} className="space-y-6">
          <fieldset disabled={editViewOnly} className={editViewOnly ? 'opacity-90' : ''}>
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden border-2 border-dashed border-slate-300">
                  {formData.photo_url ? (
                    <img src={formData.photo_url} alt="Profil" className="w-full h-full object-cover" />
                  ) : (
                    <Camera className="w-8 h-8 text-slate-400" />
                  )}
                </div>
                {!editViewOnly && (
                  <>
                    <Label htmlFor="edit-photo-upload" className="absolute bottom-0 right-0 p-1.5 bg-white rounded-full border shadow-sm cursor-pointer hover:bg-slate-50">
                      <Camera className="w-4 h-4 text-slate-600" />
                    </Label>
                    <Input
                      id="edit-photo-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleFileUpload(e, 'photo')}
                    />
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>TC Kimlik No <span className="text-red-500">*</span></Label>
                <Input 
                  required 
                  maxLength={11}
                  value={formData.tc_no || ''}
                  onChange={(e) => setFormData({ ...formData, tc_no: e.target.value })}
                  disabled={editViewOnly}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Ad Soyad <span className="text-red-500">*</span></Label>
                <Input 
                  required 
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>E-mail Adresi</Label>
                <Input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="ornek@fortis.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Doğum Tarihi</Label>
                <Input
                  type="date"
                  value={formData.birth_date || ''}
                  onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Cinsiyet</Label>
                <Select value={formData.gender || ''} onValueChange={(value) => setFormData({ ...formData, gender: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seçiniz" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="erkek">Erkek</SelectItem>
                    <SelectItem value="kadın">Kadın</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Medeni Durum</Label>
                <Select value={formData.marital_status || ''} onValueChange={(value) => setFormData({ ...formData, marital_status: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seçiniz" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bekar">Bekar</SelectItem>
                    <SelectItem value="evli">Evli</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Çocuk Sayısı</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.children_count || 0}
                  onChange={(e) => setFormData({ ...formData, children_count: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Öğrenim Durumu</Label>
                <Select value={formData.education_level || ''} onValueChange={(value) => setFormData({ ...formData, education_level: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seçiniz" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ilkokul">İlkokul</SelectItem>
                    <SelectItem value="ortaokul">Ortaokul</SelectItem>
                    <SelectItem value="lise">Lise</SelectItem>
                    <SelectItem value="universite">Üniversite</SelectItem>
                    <SelectItem value="yuksek_lisans">Yüksek Lisans</SelectItem>
                    <SelectItem value="doktora">Doktora</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Şube <span className="text-red-500">*</span></Label>
                <Select 
                  value={formData.branch_id} 
                  onValueChange={(value) => setFormData({ ...formData, branch_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Şube Seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches?.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Unvan / Görev <span className="text-red-500">*</span></Label>
                <Select 
                  value={formData.position_id} 
                  onValueChange={(value) => setFormData({ ...formData, position_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Görev Seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {positions?.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Meslek</Label>
                <Select 
                  value={formData.profession} 
                  onValueChange={(value) => setFormData({ ...formData, profession: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Meslek Seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {professions?.map(p => (
                      <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input 
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>

              <div className="col-span-2 space-y-2">
                <Label>Adres</Label>
                <Input
                  value={formData.address || ''}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Acil Durum İletişim</Label>
                <Input
                  value={formData.emergency_contact || ''}
                  onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                />
              </div>

              {/* Sağlık Bilgileri */}
              <div className="col-span-2 mt-4">
                <h3 className="text-sm font-semibold mb-2 text-red-700">Sağlık Bilgileri</h3>
              </div>
              <div className="space-y-2">
                <Label>Kan Grubu</Label>
                <Select value={formData.blood_type || ''} onValueChange={(value) => setFormData({ ...formData, blood_type: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Kan Grubu Seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A+">A RH+</SelectItem>
                    <SelectItem value="A-">A RH-</SelectItem>
                    <SelectItem value="B+">B RH+</SelectItem>
                    <SelectItem value="B-">B RH-</SelectItem>
                    <SelectItem value="AB+">AB RH+</SelectItem>
                    <SelectItem value="AB-">AB RH-</SelectItem>
                    <SelectItem value="0+">0 RH+</SelectItem>
                    <SelectItem value="0-">0 RH-</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Kronik Rahatsızlıklar</Label>
                <Input
                  value={formData.chronic_diseases || ''}
                  onChange={(e) => setFormData({ ...formData, chronic_diseases: e.target.value })}
                  placeholder="Varsa belirtin"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Alerjenler</Label>
                <Input
                  value={formData.allergies || ''}
                  onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                  placeholder="Varsa belirtin (ilaç, gıda, vb.)"
                />
              </div>

              {/* İş Bilgileri */}
              <div className="col-span-2 mt-4">
                <h3 className="text-sm font-semibold mb-2">İş Bilgileri</h3>
              </div>
              <div className="space-y-2">
                <Label>İşe Giriş Tarihi <span className="text-red-500">*</span></Label>
                <Input 
                  type="date" 
                  required 
                  value={formData.hire_date}
                  onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Departman</Label>
                <Input
                  value={formData.department || ''}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Sözleşme Tipi</Label>
                <Select value={formData.contract_type || ''} onValueChange={(value) => setFormData({ ...formData, contract_type: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seçiniz" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="belirsiz">Belirsiz Süreli</SelectItem>
                    <SelectItem value="belirli">Belirli Süreli</SelectItem>
                    <SelectItem value="part-time">Part-Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Durum <span className="text-red-500">*</span></Label>
                <Select value={formData.employment_status || 'active'} onValueChange={(value) => setFormData({ ...formData, employment_status: value })} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Durum seçiniz" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Aktif</SelectItem>
                    <SelectItem value="on_leave">İzinli</SelectItem>
                    <SelectItem value="terminated">Ayrılmış</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Başlangıç Maaşı</Label>
                <Input 
                  type="number" 
                  value={formData.starting_salary}
                  disabled
                  className="bg-slate-100"
                />
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Başlangıç maaşı değiştirilemez</p>
              </div>

              {/* Maaş Güncelleme Bölümü */}
              <div className="col-span-2 mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold mb-3 text-green-800 flex items-center gap-2">
                  💰 Maaş Güncelleme
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Güncel Maaş</Label>
                    <div className="text-lg font-bold text-green-700">
                      ₺{selectedEmployee?.current_salary?.toLocaleString('tr-TR') || '0'}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new_salary">Yeni Maaş</Label>
                    <div className="flex gap-2">
                      <Input
                        id="new_salary"
                        type="number"
                        placeholder="Yeni maaş tutarı"
                        value={newSalaryAmount || ''}
                        onChange={(e) => setNewSalaryAmount(e.target.value)}
                        className="flex-1 bg-white"
                      />
                      <Button
                        type="button"
                        onClick={handleSalaryUpdate}
                        className="bg-green-600 hover:bg-green-700"
                        disabled={!newSalaryAmount}
                      >
                        Güncelle
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Maaş Geçmişi */}
                {selectedEmployee?.salary_history && selectedEmployee.salary_history.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-green-200">
                    <h4 className="text-sm font-medium mb-2 text-green-700">Maaş Geçmişi</h4>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {[...selectedEmployee.salary_history].reverse().map((salary, idx) => (
                        <div key={idx} className="flex justify-between text-sm bg-white rounded px-2 py-1">
                          <span className="font-medium">₺{salary.amount?.toLocaleString('tr-TR')}</span>
                          <span className="text-[hsl(var(--muted-foreground))]">
                            {salary.effective_date || salary.date || '-'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Banka ve Sigorta Bilgileri */}
              <div className="col-span-2 mt-4">
                <h3 className="text-sm font-semibold mb-2">Banka ve Sigorta Bilgileri</h3>
              </div>
              <div className="space-y-2">
                <Label>Banka Adı</Label>
                <Input
                  value={formData.bank_name || ''}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  placeholder="Örn: Ziraat Bankası"
                />
              </div>
              <div className="space-y-2">
                <Label>IBAN</Label>
                <Input
                  value={formData.iban || ''}
                  onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                  placeholder="TR00 0000 0000 0000 0000 0000 00"
                />
              </div>
              <div className="space-y-2">
                <Label>Sigorta Numarası</Label>
                <Input
                  value={formData.insurance_number || ''}
                  onChange={(e) => setFormData({ ...formData, insurance_number: e.target.value })}
                  placeholder="SGK Sicil No"
                />
              </div>

              {/* Prim Bilgileri */}
              <div className="col-span-2 mt-4">
                <h3 className="text-sm font-semibold mb-2">Prim Bilgileri</h3>
              </div>
              <div className="col-span-2 space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="edit_has_bonus"
                    checked={formData.has_bonus || false}
                    onChange={(e) => setFormData({ ...formData, has_bonus: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="edit_has_bonus">Prim var</Label>
                </div>
              </div>
              {formData.has_bonus && (
                <>
                  <div className="space-y-2">
                    <Label>Prim Tipi</Label>
                    <Select value={formData.bonus_type || ''} onValueChange={(value) => setFormData({ ...formData, bonus_type: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Prim tipi seçiniz" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="performans">Performans</SelectItem>
                        <SelectItem value="satış">Satış</SelectItem>
                        <SelectItem value="üretim">Üretim</SelectItem>
                        <SelectItem value="diğer">Diğer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Prim Açıklaması</Label>
                    <Input
                      value={formData.bonus_description || ''}
                      onChange={(e) => setFormData({ ...formData, bonus_description: e.target.value })}
                      placeholder="Prim detaylarını yazınız..."
                    />
                  </div>
                </>
              )}

              {/* Ek Yardımlar */}
              <div className="col-span-2 mt-4">
                <h3 className="text-sm font-semibold mb-2">Ek Yardımlar</h3>
              </div>
              
              <div className="col-span-2 space-y-2">
                <div className="flex items-center gap-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="edit_support_yemek"
                      checked={selectedSupports?.yemek_karti?.enabled || false}
                      onChange={(e) => setSelectedSupports({
                        ...selectedSupports,
                        yemek_karti: { ...selectedSupports.yemek_karti, enabled: e.target.checked }
                      })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="edit_support_yemek">Yemek Kartı</Label>
                  </div>
                  {selectedSupports?.yemek_karti?.enabled && (
                    <Input
                      type="number"
                      placeholder="Tutar (TL)"
                      value={selectedSupports.yemek_karti.amount || ''}
                      onChange={(e) => setSelectedSupports({
                        ...selectedSupports,
                        yemek_karti: { ...selectedSupports.yemek_karti, amount: e.target.value }
                      })}
                      className="w-40"
                    />
                  )}
                </div>
              </div>

              <div className="col-span-2 space-y-2">
                <div className="flex items-center gap-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="edit_support_yol"
                      checked={selectedSupports?.yol_parasi?.enabled || false}
                      onChange={(e) => setSelectedSupports({
                        ...selectedSupports,
                        yol_parasi: { ...selectedSupports.yol_parasi, enabled: e.target.checked }
                      })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="edit_support_yol">Yol Parası</Label>
                  </div>
                  {selectedSupports?.yol_parasi?.enabled && (
                    <Input
                      type="number"
                      placeholder="Tutar (TL)"
                      value={selectedSupports.yol_parasi.amount || ''}
                      onChange={(e) => setSelectedSupports({
                        ...selectedSupports,
                        yol_parasi: { ...selectedSupports.yol_parasi, amount: e.target.value }
                      })}
                      className="w-40"
                    />
                  )}
                </div>
              </div>

              <div className="col-span-2 space-y-2">
                <div className="flex items-center gap-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="edit_support_saglik"
                      checked={selectedSupports?.saglik_sigortasi?.enabled || false}
                      onChange={(e) => setSelectedSupports({
                        ...selectedSupports,
                        saglik_sigortasi: { ...selectedSupports.saglik_sigortasi, enabled: e.target.checked }
                      })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="edit_support_saglik">Sağlık Sigortası</Label>
                  </div>
                  {selectedSupports?.saglik_sigortasi?.enabled && (
                    <Input
                      type="number"
                      placeholder="Tutar (TL)"
                      value={selectedSupports.saglik_sigortasi.amount || ''}
                      onChange={(e) => setSelectedSupports({
                        ...selectedSupports,
                        saglik_sigortasi: { ...selectedSupports.saglik_sigortasi, amount: e.target.value }
                      })}
                      className="w-40"
                    />
                  )}
                </div>
              </div>
            </div>
          </fieldset>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setShowEditModal(false); resetForm(); }}>
              {editViewOnly ? 'Kapat' : 'İptal'}
            </Button>
            {!editViewOnly && (
              <Button type="submit" className="bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]">
                Kaydet
              </Button>
            )}
          </DialogFooter>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditEmployeeModal;
